<template>
  <span class="custom-report-button" data-test="custom-report-button-wrapper">
    <Dropdown ref="dropdown" data-test="custom-report-dropdown">
      <button
        class="dropdown-toggle btn btn-default custom-report-button-btn"
        :class="btnClass"
        data-test="custom-report-button"
        type="button"
      >
        Create <span class="caret"></span>
      </button>
      <template #dropdown>
        <template v-for="group in allGroups">
          <li
            v-if="group.id && group.name && group.memberTags.length > 1 && !deletedGroups[group.id]"
            :key="group.id"
            style="position: relative"
            class=""
            data-test="custom-report-dropdown-item"
          >
            <a
              @click="isError(group) ? edit(group, true) : view(group)"
              @keydown.space="isError(group) ? edit(group, true) : view(group)"
              role="button"
              :style="canEdit ? 'margin-right: 61px' : ''"
              tabindex="0"
            >
              <s v-if="isError(group)">
                {{ group.name }}
              </s>
              <span v-else>
                {{ group.name }}
              </span>
            </a>

            <a
              @click="edit(group)"
              @keydown.space="edit(group)"
              v-if="canEdit"
              role="button"
              aria-label="edit"
              style="
                position: absolute;
                right: 31px;
                top: 0;
                padding: 3px;
                width: 30px;
                text-align: center;
              "
              data-test="custom-report-edit-item-link"
              tabindex="0"
            >
              <span class="glyphicon glyphicon-pencil"></span>
            </a>

            <a
              @click="del(group)"
              @keydown.space="del(group)"
              v-if="canEdit"
              aria-label="delete"
              role="button"
              style="
                position: absolute;
                right: 1px;
                top: 0;
                padding: 3px;
                width: 30px;
                text-align: center;
              "
              data-test="custom-report-delete-item-link"
              tabindex="0"
            >
              <span class="glyphicon glyphicon-trash"></span>
            </a>
          </li>
        </template>
        <li role="separator" class="divider"></li>
        <li v-if="!noEditBecauseNotLeader">
          <a
            role="button"
            @click="create()"
            @keydown.space="create()"
            class=""
            data-test="custom-report-create-button"
            tabindex="0"
            ><span class="glyphicon glyphicon-plus"></span> Create a custom report
          </a>
        </li>
        <li class="" v-if="noEditBecauseNotLeader">
          <a>Your team lead(s) can create reports for sub-teams here.</a>
        </li>
      </template>
    </Dropdown>
    <div v-for="group in allGroups" :key="'editor.' + group.id">
      <CustomReportEditor
        :patchUrl="patchUrl"
        :redirectUrlBase="redirectUrlBase"
        :group="group"
        ref="editor"
        :availableMemberTags="availableMemberTags"
        v-on="$attrs"
      />
    </div>

    <div v-for="group in allGroups" :key="'deleter.' + group.id">
      <CustomReportDeleter
        :deleteUrlBase="deleteUrlBase"
        :group="group"
        @deleted="deleted(group)"
        ref="deleter"
        v-on="$attrs"
      />
    </div>
  </span>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, onMounted } from "vue";
import { v4 as uuid } from "uuid";

import { Dropdown } from "uiv";
import { CustomReportApiModel } from "../api-models";
import { validationErrors, CustomReportMemberTag } from "./custom-report-api-model";

import CustomReportEditor from "./custom-report-editor.vue";
import CustomReportDeleter from "./custom-report-deleter.vue";

const props = withDefaults(
  defineProps<{
    id: string;
    noEditBecauseNotLeader?: boolean;
    groups?: CustomReportApiModel[];
    kind?: "Viewpoints" | "Team" | "TeamViewpoints";
    btnClass?: string;
    availableMemberTags: CustomReportMemberTag[];
  }>(),
  {
    noEditBecauseNotLeader: false,
    groups: () => [],
    kind: "Team",
    btnClass: "",
  },
);

const dropdown = ref<typeof Dropdown>();

const editor = ref<InstanceType<typeof CustomReportEditor>[]>([]);

const deleter = ref<InstanceType<typeof CustomReportDeleter>[]>([]);

const patchUrl = computed(() => {
  if (props.kind === "Viewpoints") {
    return "/api/customviewpointsreport/";
  }
  return `/api/customteamreport/${props.id}`;
});

const redirectUrlBase = computed(() => {
  if (props.kind === "Team") {
    return "/teamreport/view/";
  }
  if (props.kind === "TeamViewpoints") {
    return "/teamviewpointsreport/view/";
  }
  if (props.kind === "Viewpoints") {
    return "/viewpoints/view/";
  }
  return undefined;
});

const deleteUrlBase = computed(() => {
  if (props.kind === "Viewpoints") {
    return "/api/customviewpointsreport/";
  }
  return "/api/customteamreport/";
});

const newGroups = ref<CustomReportApiModel[]>([]);
const deletedGroups = ref<{ [id: string]: boolean }>({});

const canEdit = computed(() => {
  return !props.noEditBecauseNotLeader;
});

const allGroups = computed(() => {
  return props.groups.concat(newGroups.value);
});

const isError = (param: CustomReportApiModel): boolean => {
  return !!validationErrors(param)[0];
};

const edit = (group: CustomReportApiModel, validate?: boolean): void => {
  const editors = editor.value;
  const currentEditor = editors.filter((ed) => ed.getGroup().id === group.id)[0];

  if (validate) {
    currentEditor.validate();
  }
  currentEditor.open();
};

const create = (): void => {
  const newGroup: CustomReportApiModel = {
    id: uuid(),
    name: "",
    memberTags: [],
    kind: "Team",
  };
  newGroup.kind = props.kind;
  newGroups.value.push(newGroup);
  nextTick(() => edit(allGroups.value[allGroups.value.length - 1]));
};

const del = (group: CustomReportApiModel): void => {
  const deleters = deleter.value;
  const currentDeleter = deleters.filter((de) => de.getGroup().id === group.id)[0];
  currentDeleter.open();
};

const deleted = (group: CustomReportApiModel): void => {
  deletedGroups.value[group.id!] = true;
};

const view = (param: string | CustomReportApiModel): void => {
  let reportId: string;
  if (typeof param === "object") {
    if (!param.id) {
      throw new Error("This Group/Custom Report is missing an id.");
    }
    reportId = param.id;
  } else {
    reportId = param;
  }

  const href = `${redirectUrlBase.value}/${reportId}`.replace("//", "/");
  window.open(href, "_blank");
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../styles/bootstrap/bootstrap.less";
@import (reference) "../styles/bootstrap/variables.less";

.text-big {
  .custom-report-button {
    li,
    span,
    div {
      font-size: 18px;
    }

    .custom-report-button-btn {
      .btn-lg;
    }
  }
}

.custom-report-button {
  .divider:first-child {
    display: none;
  }
}
</style>
